import './QueryExporter.scss';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MdAdd } from 'react-icons/md';
import { Role } from '@blisspointmedia/bpm-types/dist/Users';
import { Button } from '@Components/Button';
import { Placement, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { ModalEditWidget } from '@Components/ModalEditWidget/ModalEditWidget';
import {
  ModalEditWidgetHandle,
  SaveChangesInput,
  SelectorOptions
} from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page/Page';
import { Spinner } from '@Components/Spinner';
import { TableEmptyState } from '@Components/TableEmptyState';
import { TruncatedTextTooltip } from '@Components/TruncatedTextTooltip';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { roleSelector } from '@src/redux/user';
import {
  buildQueryExporterHeaders,
  getQueryRowId,
  PAGE_TITLE,
  PAGE_TYPE,
  QueryRow,
  ROW_HEIGHT,
  TIMEZONE_OPTIONS
} from './query-exporter-constants';
import {
  checkQueryRowCanDelete,
  checkQueryRowIsValid,
  getQueryRowMetadataLines,
  isCronInvalid
} from './query-exporter-utils';
import { useQueryExporterData } from './use-query-exporter';

const SELECTOR_OPTIONS: SelectorOptions = {
  tz: TIMEZONE_OPTIONS.map(tz => ({ label: tz, value: tz }))
};

export const QueryExporter = () => {
  const { data, isLoading, error, save, isSaving, runQuery } = useQueryExporterData();
  const setError = useSetError();
  const isNOC = useAppSelector(roleSelector) === Role.NOC;
  const widgetRef = useRef<ModalEditWidgetHandle<QueryRow>>(null);

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load query exporter data",
        message: `Failed to get query export data. ERROR: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const headers = useMemo(() => buildQueryExporterHeaders(runQuery, isCronInvalid), [runQuery]);

  const onSaveModal = useCallback(
    async ({ addedData, modifiedData, deletedData, cleanup }: SaveChangesInput<QueryRow>) => {
      try {
        await save({ insert: addedData, update: modifiedData, delete: deletedData });
        cleanup();
      } catch (e) {
        setError({
          message: `Failed to update exported queries. ERROR: ${e.message}`,
          reportError: e
        });
      }
    },
    [save, setError]
  );

  return (
    <Page
      title={PAGE_TITLE}
      pageType={PAGE_TYPE}
      isLoading={isLoading}
      actions={isSaving ? <Spinner color='white' /> : undefined}
    >
      <div className='query-exporter-page'>
        <ModalEditWidget<QueryRow>
          ref={widgetRef}
          className='query-exporter-table'
          dataTestId='query-exporter-table'
          headers={headers}
          initialTableData={data ?? []}
          onSaveModal={onSaveModal}
          saveOnEdit
          selectorOptions={SELECTOR_OPTIONS}
          enableAdd={!isNOC}
          readOnly={isNOC}
          checkIsValid={checkQueryRowIsValid}
          checkCanDelete={checkQueryRowCanDelete}
          rowHeight={ROW_HEIGHT}
          footerMetadata={renderFooterMetadata}
          getRowId={getQueryRowId}
          isLoading={isLoading}
          emptyContent={
            <EmptyContent isNOC={isNOC} onAddClick={() => widgetRef.current?.openAddModal()} />
          }
        />
      </div>
    </Page>
  );
};

const renderFooterMetadata = (row: Partial<QueryRow>) => (
  <>
    {getQueryRowMetadataLines(row).map(line => (
      <TruncatedTextTooltip
        key={line}
        text={line}
        className='metadata-line'
        placement={Placement.TOP}
      />
    ))}
  </>
);

interface EmptyContentProps {
  isNOC: boolean;
  onAddClick: () => void;
}

// ModalEditWidget hides its own header Add button whenever emptyContent is set and the table has
// zero rows -- it expects the empty state to own the CTA instead, so the non-NOC branch here is
// that CTA (via the imperative openAddModal, since we're not using customEditor).
const EmptyContent = ({ isNOC, onAddClick }: EmptyContentProps) => {
  if (isNOC) return <TableEmptyState />;
  return (
    <EmptyState title='No queries yet.' subtitle='Add a query to get started.'>
      <Button size={Size.LG} leadingElement={<MdAdd />} onClick={onAddClick}>
        Add Query
      </Button>
    </EmptyState>
  );
};
